/**
 * Supabase `functions.invoke` often returns a generic
 * "Edge Function returned a non-2xx status code" Error while the real
 * `{ error: "..." }` body is on `data` or `error.context` (Response).
 * Returns that JSON body when available.
 */
export async function readEdgeFunctionErrorBody(
    error: unknown,
    data: unknown
): Promise<Record<string, unknown> | null> {
    if (data && typeof data === "object") {
        return data as Record<string, unknown>;
    }

    const context =
        error && typeof error === "object" && "context" in error ? (error as { context: unknown }).context : null;
    if (context instanceof Response) {
        try {
            const body = await context.clone().json();
            if (body && typeof body === "object") {
                return body as Record<string, unknown>;
            }
        } catch {
            // ignore parse failures
        }
    }

    return null;
}

function messageFromBody(body: Record<string, unknown> | null): string | null {
    if (!body) return null;
    if (typeof body.error === "string" && body.error.trim()) {
        return body.error.trim();
    }
    if (typeof body.message === "string" && body.message.trim()) {
        return body.message.trim();
    }
    return null;
}

export async function readEdgeFunctionErrorMessage(
    error: unknown,
    data: unknown,
    fallback = "Something went wrong. Please try again."
): Promise<string> {
    const fromData = data && typeof data === "object" ? messageFromBody(data as Record<string, unknown>) : null;
    if (fromData) return fromData;

    const fromContext = messageFromBody(await readEdgeFunctionErrorBody(error, null));
    if (fromContext) return fromContext;

    if (error instanceof Error && error.message.trim()) {
        const message = error.message.trim();
        if (!/non-2xx status code/i.test(message)) {
            return message;
        }
    }

    return fallback;
}

/** Clearer copy for admin create-user failures (British English). */
export function friendlyAdminCreateUserMessage(raw: string): string {
    const message = raw.trim();
    const lower = message.toLowerCase();

    if (
        lower.includes("already been registered") ||
        lower.includes("already registered") ||
        lower.includes("user already exists") ||
        lower.includes("email address has already been")
    ) {
        return "That email is already registered. Use a different email, or continue with the existing user if you already created them.";
    }

    if (lower.includes("not authorised") || lower.includes("not authorized")) {
        return "You are not authorised to create users.";
    }

    if (
        lower.includes("password should contain") ||
        lower.includes("weak password") ||
        lower.includes("weak_password")
    ) {
        return "Could not set a valid password for this user. Please try again, or ask an engineer to check the auth password policy.";
    }

    if (lower.includes("missing required")) {
        return "Please enter first name, last name, and a valid email.";
    }

    if (/non-2xx status code/i.test(message)) {
        return "Could not create the user. Please try again.";
    }

    return message || "Could not create the user. Please try again.";
}

/** Clearer copy for admin add-organisation failures. */
export function friendlyAdminAddOrganisationMessage(raw: string): string {
    const message = raw.trim();
    const lower = message.toLowerCase();

    if (lower.includes("already exists") || lower.includes("organisations_organisation_name_key")) {
        return "An organisation with this name already exists.";
    }

    if (lower.includes("not authorised") || lower.includes("not authorized")) {
        return "You are not authorised to add organisations.";
    }

    if (lower.includes("missing required")) {
        return "Please enter an organisation name and code.";
    }

    if (/non-2xx status code/i.test(message)) {
        return "Could not add the organisation. Please try again.";
    }

    return message || "Could not add the organisation. Please try again.";
}

/** Clearer copy for admin org-role RPC / PostgREST failures. Keep matchers narrow - shared outside wizard. */
export function friendlyAdminOrgRoleMessage(raw: string): string {
    const message = raw.trim();
    const lower = message.toLowerCase();

    if (
        lower.includes("admin_insert_user_organisation_role") ||
        lower.includes("pgrst202") ||
        (lower.includes("schema cache") && lower.includes("organisation_role"))
    ) {
        return "Could not add the organisation role. The database may be missing a recent update. Ask an engineer to apply migrations, then try again.";
    }

    if (lower.includes("not authorised") || lower.includes("not authorized")) {
        return "You are not authorised to assign organisation roles.";
    }

    if (
        lower.includes("users_organisations_roles_pkey") ||
        lower.includes("duplicate key value") ||
        (lower.includes("unique constraint") && lower.includes("users_organisations_roles"))
    ) {
        return "That organisation role is already assigned to this user.";
    }

    return message || "Could not update organisation role. Please try again.";
}

/** Clearer copy for admin-impersonate / magic-link failures. */
export function friendlyAdminImpersonateMessage(raw: string): string {
    const message = raw.trim();
    const lower = message.toLowerCase();

    if (lower.includes("not authorised") || lower.includes("not authorized")) {
        return "You are not authorised to impersonate users.";
    }

    if (
        lower.includes("user not found") ||
        lower.includes("no user found") ||
        lower.includes("could not find that user") ||
        lower.includes("could not find user")
    ) {
        return "Could not find that user. Check the email and try again.";
    }

    if (/non-2xx status code/i.test(message)) {
        return "Could not generate a magic link. Please try again.";
    }

    return message || "Could not generate a magic link. Please try again.";
}
